// --- FILE: src/hooks/useCsvExport.js (COMPREHENSIVE CSV EXPORT SYSTEM) ---
import { useState, useCallback } from 'react';
import { format as formatDate } from 'date-fns';
import { formatCurrency, formatDuration } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';

export const useCsvExport = () => {
  const { user, userRole } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [lastExportInfo, setLastExportInfo] = useState(null);

  /**
   * Helper para escapar valores para CSV (maneja comas, comillas y saltos de línea).
   * @param {string | number} value
   * @returns {string} Valor escapado para CSV.
   */
  const escapeCsvValue = useCallback((value) => {
    if (value === null || value === undefined) {
      return '';
    }
    let stringValue = String(value);
    // Si el valor contiene comas, comillas dobles o saltos de línea,
    // se debe encerrar entre comillas dobles y escapar las comillas internas.
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }, []);

  /**
   * Genera una cadena CSV a partir de un array de objetos.
   * La primera fila son los encabezados.
   * @param {Array<Object>} data - Array de objetos a exportar.
   * @returns {string} Contenido del archivo CSV.
   */
  const generateCsvContent = useCallback((data) => {
    if (!data || data.length === 0) {
      return '';
    }

    const headers = Object.keys(data[0]);
    const headerRow = headers.map(escapeCsvValue).join(',');

    const bodyRows = data.map(row => {
      return headers.map(header => escapeCsvValue(row[header])).join(',');
    });

    return [headerRow, ...bodyRows].join('\n');
  }, [escapeCsvValue]);

  /**
   * Dispara la descarga de un archivo CSV en el navegador.
   * @param {string} content - Contenido del archivo CSV.
   * @param {string} filename - Nombre del archivo con extensión .csv.
   */
  const downloadCsvFile = useCallback((content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) { // Feature detection for HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // Clean up the URL object
    } else {
      // Fallback for browsers that don't support the download attribute
      window.open('data:text/csv;charset=utf-8,' + encodeURIComponent(content));
    }
  }, []);

  // === FUNCIÓN PRINCIPAL PARA EXPORTAR REPORTE DE NÓMINA (a CSV) ===
  // Nota: Esto generará múltiples archivos CSV si se incluyen detalles de turnos o ventas,
  // ya que un solo archivo CSV no soporta múltiples hojas como XLSX.
  const exportPayrollReport = useCallback(async (
    reportData, 
    options = {}
  ) => {
    const {
      filename = 'reporte_nomina',
      includeShiftsDetail = true,
      includeSalesDetail = true,
      includeExecutiveSummary = true,
      dateRange = null,
      reportType = 'all'
    } = options;

    setExporting(true);
    setExportProgress(0);
    
    try {
      const timestamp = formatDate(new Date(), 'yyyy-MM-dd_HH-mm');
      
      // Información del reporte para las cabeceras del CSV
      const reportInfo = {
        'Tipo de Reporte': reportType === 'all' ? 'Completo' : 
                          reportType === 'hours' ? 'Solo Horas' : 'Solo Comisiones',
        'Período': dateRange || 'Período personalizado',
        'Generado Por': user?.email || 'Sistema',
        'Generado El': new Date().toLocaleString('es-PR'),
        'Total Empleados Activos': reportData.length,
      };

      setExportProgress(20);

      // --- REPORTE 1: Resumen General ---
      const summaryHeaders = [
        'Empleado', 'Horas Trabajadas', 'Pago por Horas', 
        'Ventas Netas', 'Órdenes', 'Comisiones', 'TOTAL A PAGAR'
      ];

      const summaryRows = reportData.map(emp => ({
        'Empleado': emp.employeeName,
        'Horas Trabajadas': formatDuration(emp.totalHours),
        'Pago por Horas': formatCurrency(emp.totalEarnings),
        'Ventas Netas': formatCurrency(emp.sales),
        'Órdenes': emp.orders,
        'Comisiones': formatCurrency(emp.commission),
        'TOTAL A PAGAR': formatCurrency(emp.totalToPay)
      }));

      // Combinar información del reporte con los datos
      const fullSummaryData = [
          Object.keys(reportInfo).map(key => escapeCsvValue(key)).join(','),
          Object.values(reportInfo).map(value => escapeCsvValue(value)).join(','),
          [], // Fila en blanco
          summaryHeaders.map(escapeCsvValue).join(',') // Encabezados de la tabla
      ].join('\n') + '\n' + summaryRows.map(row => 
          summaryHeaders.map(header => escapeCsvValue(row[header])).join(',')
      ).join('\n');

      downloadCsvFile(fullSummaryData, `${filename}_Resumen_${timestamp}.csv`);
      setExportProgress(40);

      // --- REPORTE 2: Turnos Detallados ---
      if (includeShiftsDetail) {
        const shiftsData = [];
        reportData.forEach(emp => {
          if (emp.shifts && emp.shifts.length > 0) {
            emp.shifts.forEach((shift) => {
              shiftsData.push({
                'Empleado': emp.employeeName,
                'ID Empleado': emp.employeeId,
                'ID Turno': shift.id || 'N/A',
                'Fecha': shift.startTime ? formatDate(shift.startTime.toDate(), 'dd/MM/yyyy') : 'N/A',
                'Día de la Semana': shift.startTime ? formatDate(shift.startTime.toDate(), 'EEEE') : 'N/A',
                'Hora Entrada': shift.startTime ? formatDate(shift.startTime.toDate(), 'HH:mm') : 'N/A',
                'Hora Salida': shift.endTime ? formatDate(shift.endTime.toDate(), 'HH:mm') : 'En progreso',
                'Duración (Horas)': (shift.durationMinutes / 60).toFixed(2),
                'Ganancias del Turno': formatCurrency(shift.earnings || 0),
                'Estado': shift.status === 'completed' ? 'Completado' : 'Activo',
                'Creado': shift.createdAt ? formatDate(shift.createdAt.toDate(), 'dd/MM/yyyy HH:mm') : 'N/A',
                'Última Modificación': shift.updatedAt ? formatDate(shift.updatedAt.toDate(), 'dd/MM/yyyy HH:mm') : 'N/A',
                'Editado Por': shift.editedBy || 'Sistema',
                'Notas': shift.notes || ''
              });
            });
          }
        });

        if (shiftsData.length > 0) {
          const shiftsCsv = generateCsvContent(shiftsData);
          downloadCsvFile(shiftsCsv, `${filename}_TurnosDetalle_${timestamp}.csv`);
        }
      }
      setExportProgress(70);

      // --- REPORTE 3: Detalles de Ventas ---
      if (includeSalesDetail) {
        const salesData = [];
        reportData.forEach(emp => {
          if (emp.orderDetails && emp.orderDetails.length > 0) {
            emp.orderDetails.forEach(order => {
              salesData.push({
                'Vendedor': emp.employeeName,
                'ID Empleado': emp.employeeId,
                'Número de Orden': order.orderNumber,
                'Fecha de Orden': formatDate(new Date(order.orderDate), 'dd/MM/yyyy HH:mm'),
                'Subtotal': formatCurrency(order.subtotal),
                'Canal': order.channel === 'pos' ? 'Punto de Venta' : 'Tienda Online',
                'Tipo': order.isLegacy ? 'Legacy (Web)' : 'Moderno (POS)',
                'Cliente': order.customerName || 'Sin nombre',
                'ID Cliente': order.customerId || 'N/A',
                'Comisión (Estimada 2%)': formatCurrency(order.subtotal * 0.02) // Asumiendo tasa fija para exportación
              });
            });
          }
        });

        if (salesData.length > 0) {
          const salesCsv = generateCsvContent(salesData);
          downloadCsvFile(salesCsv, `${filename}_VentasDetalle_${timestamp}.csv`);
        }
      }
      setExportProgress(90);

      const exportInfo = {
        filename: `${filename}_${timestamp}`, // Nombre base, ya que se generan múltiples archivos
        timestamp: new Date(),
        recordCount: reportData.length,
        type: 'payroll_csv',
        user: user?.email || 'Sistema'
      };
      
      setLastExportInfo(exportInfo);
      setExportProgress(100);

      setTimeout(() => setExportProgress(0), 2000);

      return {
        success: true,
        filename: `Múltiples archivos CSV para ${filename}_${timestamp}`,
        recordCount: reportData.length,
        exportInfo
      };

    } catch (error) {
      console.error('Error exportando reporte de nómina (CSV):', error);
      return {
        success: false,
        error: error.message || 'Error desconocido al exportar a CSV'
      };
    } finally {
      setExporting(false);
    }
  }, [user, downloadCsvFile, generateCsvContent, escapeCsvValue]);

  // === FUNCIÓN PARA EXPORTAR SOLO TURNOS (a CSV) ===
  const exportShiftsOnly = useCallback(async (
    shiftsData, 
    employeeName = 'Todos',
    options = {}
  ) => {
    const {
      filename = `turnos_${employeeName.replace(/\s+/g, '_')}`,
      dateRange = null
    } = options;

    setExporting(true);
    setExportProgress(0);
    
    try {
      const timestamp = formatDate(new Date(), 'yyyy-MM-dd_HH-mm');

      setExportProgress(25);

      const formattedShifts = shiftsData.map((shift, index) => ({
        'Turno #': index + 1,
        'Empleado': shift.employeeName || employeeName,
        'ID Turno': shift.id || 'N/A',
        'Fecha': shift.startTime ? formatDate(shift.startTime.toDate(), 'dd/MM/yyyy') : 'N/A',
        'Día': shift.startTime ? formatDate(shift.startTime.toDate(), 'EEEE') : 'N/A',
        'Hora Entrada': shift.startTime ? formatDate(shift.startTime.toDate(), 'HH:mm:ss') : 'N/A',
        'Hora Salida': shift.endTime ? formatDate(shift.endTime.toDate(), 'HH:mm:ss') : 'En progreso',
        'Duración Total': formatDuration(shift.durationMinutes / 60),
        'Duración (Horas Decimales)': (shift.durationMinutes / 60).toFixed(2),
        'Minutos Trabajados': shift.durationMinutes || 0,
        'Tarifa por Hora': formatCurrency(shift.hourlyRate || 10.50), // Asegúrate de que hourlyRate esté disponible aquí
        'Ganancias': formatCurrency(shift.earnings || 0),
        'Estado': shift.status === 'completed' ? 'Completado' : 'Activo',
        'Fecha de Creación': shift.createdAt ? formatDate(shift.createdAt.toDate(), 'dd/MM/yyyy HH:mm') : 'N/A',
        'Última Edición': shift.updatedAt ? formatDate(shift.updatedAt.toDate(), 'dd/MM/yyyy HH:mm') : 'N/A',
        'Editado Por': shift.editedBy || 'Sistema'
      }));

      setExportProgress(75);

      const csvContent = generateCsvContent(formattedShifts);
      const finalFilename = `${filename}_${timestamp}.csv`;
      downloadCsvFile(csvContent, finalFilename);

      setExportProgress(100);
      setTimeout(() => setExportProgress(0), 2000);

      return {
        success: true,
        filename: finalFilename,
        recordCount: shiftsData.length
      };

    } catch (error) {
      console.error('Error exportando turnos (CSV):', error);
      return {
        success: false,
        error: error.message || 'Error al exportar turnos a CSV'
      };
    } finally {
      setExporting(false);
    }
  }, [downloadCsvFile, generateCsvContent]);

  // === FUNCIÓN PARA EXPORTAR VENTAS UNIFICADAS (a CSV) ===
  const exportUnifiedSales = useCallback(async (
    unifiedSalesData,
    options = {}
  ) => {
    const {
      filename = 'ventas_unificadas',
      includeOrderDetails = true,
      includeChannelBreakdown = true
    } = options;

    setExporting(true);
    setExportProgress(0);
    
    try {
      const timestamp = formatDate(new Date(), 'yyyy-MM-dd_HH-mm');

      setExportProgress(20);

      // --- REPORTE 1: Resumen de Vendedores ---
      const sellersData = Object.entries(unifiedSalesData.sellers).map(([name, data]) => ({
        'Vendedor': name,
        'Ventas Totales': formatCurrency(data.sales),
        'Número de Órdenes': data.orders,
        'Comisiones': formatCurrency(data.commission),
        'Tasa de Comisión': data.commissionRate + '%',
        'Órdenes Legacy': data.legacyOrders || 0,
        'Órdenes Modernas': data.modernOrders || 0,
        'Promedio por Orden': data.orders > 0 ? formatCurrency(data.sales / data.orders) : formatCurrency(0)
      }));
      const sellersCsv = generateCsvContent(sellersData);
      downloadCsvFile(sellersCsv, `${filename}_Vendedores_${timestamp}.csv`);

      setExportProgress(50);

      // --- REPORTE 2: Breakdown por Canal ---
      if (includeChannelBreakdown && unifiedSalesData.channelBreakdown) {
        const channelData = [
          {
            'Canal': 'Punto de Venta (POS)',
            'Órdenes': unifiedSalesData.channelBreakdown.pos.orders,
            'Ventas': formatCurrency(unifiedSalesData.channelBreakdown.pos.sales),
            'Comisiones': formatCurrency(unifiedSalesData.channelBreakdown.pos.commissions),
            '% del Total': ((unifiedSalesData.channelBreakdown.pos.sales / unifiedSalesData.totalSales) * 100).toFixed(1) + '%'
          },
          {
            'Canal': 'Tienda Online (Web)',
            'Órdenes': unifiedSalesData.channelBreakdown.web.orders,
            'Ventas': formatCurrency(unifiedSalesData.channelBreakdown.web.sales),
            'Comisiones': formatCurrency(unifiedSalesData.channelBreakdown.web.commissions),
            '% del Total': ((unifiedSalesData.channelBreakdown.web.sales / unifiedSalesData.totalSales) * 100).toFixed(1) + '%'
          },
          {
            'Canal': 'TOTAL', // Fila para el total
            'Órdenes': unifiedSalesData.totalOrders,
            'Ventas': formatCurrency(unifiedSalesData.totalSales),
            'Comisiones': formatCurrency(unifiedSalesData.totalCommissions),
            '% del Total': '100%'
          }
        ];
        const channelCsv = generateCsvContent(channelData);
        downloadCsvFile(channelCsv, `${filename}_Canales_${timestamp}.csv`);
      }

      setExportProgress(80);

      // --- REPORTE 3: Detalles de Órdenes ---
      if (includeOrderDetails) {
        const orderDetails = [];
        Object.entries(unifiedSalesData.sellers).forEach(([sellerName, sellerData]) => {
          if (sellerData.orderDetails) {
            sellerData.orderDetails.forEach(order => {
              orderDetails.push({
                'Vendedor': sellerName,
                'Número de Orden': order.orderNumber,
                'Fecha': formatDate(new Date(order.orderDate), 'dd/MM/yyyy HH:mm'),
                'Subtotal': formatCurrency(order.subtotal),
                'Canal': order.channel === 'pos' ? 'POS' : 'Web',
                'Tipo de Orden': order.isLegacy ? 'Legacy' : 'Moderna',
                'Cliente': order.customerName || 'Sin nombre',
                'ID Cliente': order.customerId || 'N/A'
              });
            });
          }
        });

        if (orderDetails.length > 0) {
          const ordersCsv = generateCsvContent(orderDetails);
          downloadCsvFile(ordersCsv, `${filename}_OrdenesDetalle_${timestamp}.csv`);
        }
      }

      setExportProgress(100);
      setTimeout(() => setExportProgress(0), 2000);

      return {
        success: true,
        filename: `Múltiples archivos CSV para ${filename}_${timestamp}`,
        recordCount: Object.keys(unifiedSalesData.sellers).length
      };

    } catch (error) {
      console.error('Error exportando ventas unificadas (CSV):', error);
      return {
        success: false,
        error: error.message || 'Error al exportar ventas a CSV'
      };
    } finally {
      setExporting(false);
    }
  }, [downloadCsvFile, generateCsvContent]);

  // === FUNCIÓN PARA EXPORTAR TEMPLATE DE TURNOS (a CSV) ===
  const exportShiftsTemplate = useCallback(async (employees = []) => {
    setExporting(true);
    
    try {
      const timestamp = formatDate(new Date(), 'yyyy-MM-dd_HH-mm');

      // Crear template con ejemplos
      const templateData = [
        {
          'ID Empleado': employees[0]?.id || 'emp_001',
          'Nombre Empleado': employees[0]?.name || 'Ejemplo Empleado',
          'Fecha (DD/MM/YYYY)': '01/01/2025',
          'Hora Entrada (HH:MM)': '09:00',
          'Hora Salida (HH:MM)': '17:00',
          'Notas': 'Turno regular'
        }
      ];

      // Agregar empleados existentes como opciones
      // Aseguramos que solo los datos relevantes para el template son incluidos
      employees.forEach(emp => {
        templateData.push({
          'ID Empleado': emp.id,
          'Nombre Empleado': emp.name,
          'Fecha (DD/MM/YYYY)': '',
          'Hora Entrada (HH:MM)': '',
          'Hora Salida (HH:MM)': '',
          'Notas': ''
        });
      });

      const csvContent = generateCsvContent(templateData);
      const filename = `template_turnos_${timestamp}.csv`;
      downloadCsvFile(csvContent, filename);

      setExporting(false);
      return {
        success: true,
        filename
      };

    } catch (error) {
      console.error('Error creando template (CSV):', error);
      setExporting(false);
      return {
        success: false,
        error: error.message
      };
    }
  }, [downloadCsvFile, generateCsvContent]);

  return {
    // Estados
    exporting,
    exportProgress,
    lastExportInfo,

    // Funciones principales
    exportPayrollReport,
    exportShiftsOnly,
    exportUnifiedSales,
    exportShiftsTemplate,

    // Utilidades
    isExporting: exporting,
    canExport: userRole === 'admin' || userRole === 'employee'
  };
};